import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { PlanComparison } from "@/components/PlanComparison";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { toast } from "sonner";

export default function Register() {
  const { t, lang } = useLang();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await register({ ...form, language: lang });
      toast.success(`Welcome, ${u.name}!`);
      navigate("/scan");
    } catch (e2) {
      setErr(apiError(e2));
    } finally { setLoading(false); }
  };

  return (
    <div className="mx-auto grid max-w-4xl gap-8 lg:grid-cols-2">
      <div>
        <div className="mb-6 flex flex-col items-center text-center lg:items-start lg:text-left">
          <Logo size={64} />
          <h1 className="mt-4 text-2xl font-bold text-gray-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("auth.registerTitle")}</h1>
          <p className="mt-1 text-sm text-gray-500">{t("auth.registerSub")}</p>
        </div>
        <form onSubmit={submit} className="rounded-2xl border border-gray-200 bg-white p-6" data-testid="register-form">
          {err && <div className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-600" data-testid="register-error">{err}</div>}
          <label className="mb-1.5 block text-sm font-semibold text-gray-700">{t("auth.name")}</label>
          <input data-testid="register-name-input" required value={form.name} onChange={set("name")}
            className="mb-4 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100" placeholder="Alex Athlete" />
          <label className="mb-1.5 block text-sm font-semibold text-gray-700">{t("auth.email")}</label>
          <input data-testid="register-email-input" type="email" required value={form.email} onChange={set("email")}
            className="mb-4 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100" placeholder="you@email.com" />
          <label className="mb-1.5 block text-sm font-semibold text-gray-700">{t("auth.password")}</label>
          <div className="relative mb-5">
            <input data-testid="register-password-input" type={show ? "text" : "password"} required minLength={6} value={form.password} onChange={set("password")}
              className="w-full rounded-xl border border-gray-200 px-4 py-3 pr-11 text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100" placeholder="••••••••" />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
              {show ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <button data-testid="register-submit-button" disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 py-3.5 text-sm font-bold text-white transition-colors hover:bg-emerald-600 disabled:opacity-60">
            {loading && <Loader2 size={16} className="animate-spin" />} {t("auth.register")}
          </button>
        </form>
        <p className="mt-5 text-center text-sm text-gray-500 lg:text-left">
          {t("auth.haveAccount")} <Link to="/login" className="font-semibold text-emerald-600 hover:underline">{t("auth.signin")}</Link>
        </p>
      </div>

      <div className="lg:pt-16">
        <PlanComparison />
        <p className="mt-3 px-1 text-xs text-gray-400">
          <span className="font-semibold text-emerald-600">{t("plan.feat.pubmedStudies")}</span> & <span className="font-semibold text-emerald-600">{t("plan.feat.expertDose")}</span> — {t("plan.membersOnly")}.
        </p>
      </div>
    </div>
  );
}
